import { useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { PROJECT_GROUPS, projectsInGroup } from "../../lib/projects";
import { useCollections, separateCollections } from "../../hooks/useCollections";
import { sessionName, signOutEverywhere, useSession } from "../../session/SessionContext";

/**
 * Left-hand navigation column — the classic personal-homepage menu, with a
 * search box at the top (Yahoo-style), a short list of destinations beneath,
 * and a drop-down holding the whole network of projects and partner sites.
 */
interface NavItem {
  to: string;
  label: string;
  /** Match the path exactly (only "/" needs it). */
  end?: boolean;
}

const LINKS: NavItem[] = [
  { to: "/", label: "Home", end: true },
  { to: "/search", label: "Search" },
  { to: "/photos", label: "Photos" },
  { to: "/videos", label: "Videos" },
];

/** Destinations listed after the collections. */
const TAIL_LINKS: NavItem[] = [
  { to: "/collections", label: "Collections" },
  { to: "/blog", label: "Blog" },
  { to: "/cv", label: "CV" },
  { to: "/contact", label: "Contact" },
];

/**
 * Collapsible "Projects" menu: every project and partner site, grouped by
 * family, plus a way through to the full /projects page. Opens automatically
 * when that page is the one being viewed.
 */
function ProjectsMenu() {
  const onProjectsPage = useLocation().pathname.startsWith("/projects");
  const [open, setOpen] = useState(onProjectsPage);

  return (
    <li className="sidebar__menu">
      <button
        type="button"
        className={`sidebar__menu-toggle${onProjectsPage ? " active" : ""}`}
        aria-expanded={open}
        aria-controls="sidebar-projects"
        onClick={() => setOpen((wasOpen) => !wasOpen)}
      >
        <span className="sidebar__caret" aria-hidden="true">
          {open ? "▾" : "▸"}
        </span>
        Projects
      </button>

      <div className="sidebar__submenu" id="sidebar-projects" hidden={!open}>
        {PROJECT_GROUPS.map((group) => (
          <div key={group.id}>
            <p className="sidebar__legend sidebar__submenu-label">{group.label}</p>
            <ul className="sidebar__list sidebar__list--small">
              {projectsInGroup(group.id).map((project) => (
                <li key={project.id}>
                  {project.current ? (
                    <Link to="/">{project.short}</Link>
                  ) : (
                    <a href={project.url} target="_blank" rel="noopener noreferrer">
                      {project.short} ↗
                    </a>
                  )}
                </li>
              ))}
            </ul>
          </div>
        ))}

        <ul className="sidebar__list sidebar__list--small">
          <li>
            <NavLink
              to="/projects"
              className={({ isActive }) => (isActive ? "active" : undefined)}
            >
              All projects
            </NavLink>
          </li>
        </ul>
      </div>
    </li>
  );
}

export function Sidebar() {
  const navigate = useNavigate();
  const [q, setQ] = useState("");

  // Collections with their own section get a top-level entry, so adding one to
  // collections.json puts it straight into the navigation.
  const { collections } = useCollections();
  const sections: NavItem[] = separateCollections(collections).map((c) => ({
    to: `/collections/${c.id}`,
    label: c.label,
  }));

  function onSearch(e: React.FormEvent) {
    e.preventDefault();
    const term = q.trim();
    navigate(term ? `/search?q=${encodeURIComponent(term)}` : "/search");
  }

  return (
    <nav className="sidebar" aria-label="Primary">
      <form className="sidebar__search" onSubmit={onSearch} role="search">
        <label htmlFor="side-search" className="sidebar__legend">
          Search
        </label>
        <input
          id="side-search"
          type="search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Find it…"
          aria-label="Search the site"
        />
        <button type="submit" className="btn sidebar__go">
          Go!
        </button>
      </form>

      <p className="sidebar__legend">Navigate</p>
      <ul className="sidebar__list">
        {[...LINKS, ...sections, ...TAIL_LINKS].map((link) => (
          <li key={link.to}>
            <NavLink
              to={link.to}
              end={link.end}
              className={({ isActive }) => (isActive ? "active" : undefined)}
            >
              {link.label}
            </NavLink>
          </li>
        ))}
        <ProjectsMenu />
      </ul>

      <MemberBox />
    </nav>
  );
}

/**
 * Who is signed in, at the foot of the menu: a quiet "Member sign-in" link for
 * visitors, and for members (or the admin) a reminder that they are seeing
 * more than the public does, with the way out.
 */
function MemberBox() {
  const { session } = useSession();
  const { pathname } = useLocation();

  if (session.kind === "ANONYMOUS") {
    const next = pathname === "/signin" ? "/" : pathname;
    return (
      <p className="sidebar__member">
        <Link to={`/signin?next=${encodeURIComponent(next)}`}>Member sign-in</Link>
      </p>
    );
  }
  return (
    <div className="sidebar__member sidebar__member--in">
      <span>
        Signed in as <strong>{sessionName(session)}</strong>
      </span>
      <span>
        {session.kind === "ADMIN" ? <Link to="/admin">Console</Link> : <Link to="/account">Account</Link>}
        {" · "}
        <button type="button" className="sidebar__signout" onClick={() => void signOutEverywhere()}>
          Sign out
        </button>
      </span>
    </div>
  );
}
